import { CarOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  TimePicker,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { BsBox2 } from 'react-icons/bs';
import { IoWarningOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';

import carImage from '../../assets/img/carrrr.PNG';
import SectionCard from '../../components/sectionCard/SectionCard';
import StepFooter from '../../components/stepFooter/StepFooter';
import { formatCurrency } from '../../utils/format';
import { AddOrderContext } from './AddOrderContext';

const { TextArea } = Input;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Сонголтын картын хамгийн их өргөн — хоёр карт зэрэгцэж багтана */
const OPTION_MAX_WIDTH = 420;

/** Татан авалт хийх боломжгүй цагууд (09:00-18:00 хооронд л ажиллана) */
const DISABLED_HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 19, 20, 21, 22, 23];

/** Өөрөө хүргэж өгөх үеийн тогтмол тарифын код */
const SELF_PRICING_CODE = '3-custom';

/** Радио сонголтын карт — сонгогдсон үед хүрээ, дэвсгэрээрээ ялгарна */
const optionCardStyle = (checked) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: OPTION_MAX_WIDTH,
  margin: 0,
  padding: 12,
  border: `1px solid ${checked ? '#4bbeda' : '#dcdfe4'}`,
  backgroundColor: checked ? '#f1fafd' : '#fff',
  borderRadius: 8,
  transition: 'border-color 0.12s ease, background-color 0.12s ease',
});

/** Текст зүүн, зураг баруун захад — хооронд нь үлдсэн зай тарна */
const optionContentStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
};

/** Картын баруун захын машины зураг — текстээ шахахгүйгээр тогтмол хэмжээтэй */
const optionImageStyle = {
  width: 56,
  height: 56,
  objectFit: 'contain',
  flexShrink: 0,
};

const OptionRadio = ({ value, checked, title, description, image }) => (
  <Radio className="option_radio_card" value={value} style={optionCardStyle(checked)}>
    <div style={optionContentStyle}>
      <Space direction="vertical" size={2}>
        <Text strong>{title}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {description}
        </Text>
      </Space>
      {image && <img src={image} alt={title} style={optionImageStyle} />}
    </div>
  </Radio>
);

const Step2 = ({ prevStep, nextStep }) => {
  const [form] = Form.useForm();

  const [fields, setFields] = useState({});
  const [transportationType, setTransportationType] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);

  const {
    finalProductList,
    setDeliveryDateFinal,
    setDeliveryTimeFinal,
    setImportantNotes,
    setDescription,
    setPricingCode,
    pricingCode,
    setTotalWeight,
    setTotalVolume,
    setProcurementType,
    procurementType,
    setCarPrice,
    setFormDataStep2,
    // Бөглөж байх зуурын утгууд — refresh хийхэд сэргэнэ
    formValuesStep2,
    setFormValuesStep2,
    setField,
  } = useContext(AddOrderContext) ?? {};

  /**
   * Сонгосон тээврийн төрлийг `fields.pricing.types`-ийн түлхүүрээр хадгална.
   * Контекст дэх `pricingCode` нь `detail[key].nameCode` — 2-р алхам руу буцаж
   * ирэхэд сонголт нь тодрохын тулд түлхүүр рүү нь буцаан тулгана.
   */
  const [selectedPricing, setSelectedPricing] = useState();

  useEffect(() => {
    setReferenceLoading(true);
    axios
      .get('/api/v1/request/procurement-reference')
      .then(({ data }) => {
        setFields(data.result);
        setField(data.result);
      })
      .catch((e) => {
        console.error('procurement-reference: ', e);
      })
      .finally(() => setReferenceLoading(false));

    setTransportationType(procurementType === 'procurement');
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!pricingCode || pricingCode === SELF_PRICING_CODE) return;

    const detail = fields?.pricing?.detail ?? {};
    const matched = Object.keys(detail).find((key) => detail[key]?.nameCode === pricingCode);
    if (matched) setSelectedPricing(matched);
  }, [fields, pricingCode]);

  const totalWeight = useMemo(
    () =>
      Number(
        finalProductList
          .reduce((total, product) => total + parseFloat(product.totalUnit * product.weight), 0)
          .toFixed(6)
      ),
    [finalProductList]
  );

  const totalVolume = useMemo(
    () =>
      Number(
        finalProductList
          .reduce((total, product) => total + parseFloat(product.totalUnit * product.volume), 0)
          .toFixed(6)
      ),
    [finalProductList]
  );

  /**
   * Тээврийн сонголтыг контекстэд шууд буулгана — эс бөгөөс зөвхөн
   * "Үргэлжлүүлэх" дарсны дараа хадгалагдаж, refresh хийхэд алга болно.
   */
  const onChangeTransportation = (isProcurement) => {
    setTransportationType(isProcurement);
    setProcurementType(isProcurement ? 'procurement' : 'self-procurement');
  };

  const onChangePricing = (key) => {
    setSelectedPricing(key);
    setPricingCode(fields?.pricing?.detail?.[key]?.nameCode);
    setCarPrice(fields?.pricing?.detail?.[key]?.price);
  };

  const onFinish = (values) => {
    if (transportationType && !selectedPricing) {
      message.error('Тээврийн төрөл сонгоно уу!');
      return;
    }

    setDeliveryDateFinal(moment(values.date.$d).format('YYYY-MM-DD'));
    setDeliveryTimeFinal(moment(values.time.$d).format('HH:mm'));
    setImportantNotes(values.important);
    setDescription(values.description);
    setTotalWeight(totalWeight);
    setTotalVolume(totalVolume);

    if (transportationType) {
      setProcurementType('procurement');
    } else {
      setProcurementType('self-procurement');
      setPricingCode(SELF_PRICING_CODE);
    }

    setFormDataStep2({
      formValues: form.getFieldsValue(),
      // `set...` нь энэ рендерт хараахан тусахгүй тул утгыг дахин тооцов
      procurementType: transportationType ? 'procurement' : 'self-procurement',
      pricingCode: transportationType
        ? fields?.pricing?.detail?.[selectedPricing]?.nameCode
        : SELF_PRICING_CODE,
      totalWeight,
      totalVolume,
    });
    nextStep();
  };

  const importantOptions = (fields?.importantNotes ?? []).map((value) => ({
    value,
    label: value,
  }));

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      initialValues={{ ...formValuesStep2 }}
      onValuesChange={(_, allValues) => setFormValuesStep2(allValues)}
      onFinish={onFinish}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<MdDateRange />} title="Татан авалтын хугацаа">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Татан авах огноо"
                name="date"
                rules={[{ required: true, message: 'Хүргэх огноо оруулна уу!' }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Огноо сонгох"
                  disabledDate={(date) =>
                    (moment(date).isAfter(
                      `${moment().format('YYYY-MM-DD 19:00:00')}`,
                      'millisecond'
                    ) &&
                      moment(date).isBefore(
                        `${moment().format('YYYY-MM-DD 23:59:59')}`,
                        'millisecond'
                      )) ||
                    date < moment().subtract(1, 'day')
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Цаг сонгох"
                name="time"
                rules={[{ required: true, message: 'Хүргэх цаг оруулна уу!' }]}
                style={{ marginBottom: 0 }}
              >
                <TimePicker
                  style={{ width: '100%' }}
                  placeholder="Цаг сонгох"
                  hideDisabledOptions
                  format="HH:mm"
                  minuteStep={5}
                  disabledTime={() => ({ disabledHours: () => DISABLED_HOURS })}
                />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <SectionCard icon={<IoWarningOutline />} title="Анхаарах шаардлагатай">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} xl={16}>
              <Form.Item label="Анхаарах шаардлагатай зүйлс" name="important">
                <Select
                  mode="multiple"
                  placeholder="Сонгох"
                  options={importantOptions}
                  loading={referenceLoading}
                  optionFilterProp="label"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} xl={16}>
              <Form.Item label="Нэмэлт тэмдэглэл" name="description" style={{ marginBottom: 0 }}>
                <TextArea rows={3} placeholder="Тэмдэглэл бичих" />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <SectionCard icon={<BsBox2 />} title="Овор хэмжээ">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item label="Жин (кг)" style={{ marginBottom: 0 }}>
                <Input disabled value={totalWeight} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item label="Эзэлхүүн (м³)" style={{ marginBottom: 0 }}>
                <Input disabled value={totalVolume} />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <SectionCard icon={<CarOutlined />} title="Тээвэр">
          <Radio.Group
            value={transportationType ? 2 : 1}
            onChange={(e) => onChangeTransportation(e.target.value === 2)}
            style={{ width: '100%' }}
          >
            <Row style={{ gap: 15 }}>
              <Col>
                <OptionRadio
                  value={1}
                  checked={!transportationType}
                  title="Өөрөө хүргэж өгөх"
                  description="Агуулахад байршуулах бараагаа хүргэж өгөх бол төлбөр бодогдохгүй"
                />
              </Col>
              <Col>
                <OptionRadio
                  value={2}
                  checked={transportationType}
                  title="Машин сонгох"
                  description="Захиалгын жинд тохирсон машин сонгох ба өөрийн хүссэн цагт татан авалт хийлгэх боломжтой."
                />
              </Col>
            </Row>
          </Radio.Group>
        </SectionCard>

        {transportationType && (
          <SectionCard icon={<RiMoneyDollarCircleLine />} title="Тээврийн төрөл сонгох">
            <Spin spinning={referenceLoading}>
              <Radio.Group
                value={selectedPricing}
                onChange={(e) => onChangePricing(e.target.value)}
                style={{ width: '50%' }}
              >
                <Row style={{ gap: 15 }}>
                  {(fields?.pricing?.types ?? [])
                    .filter(
                      (value) => fields?.pricing?.detail?.[value]?.nameCode !== SELF_PRICING_CODE
                    )
                    .map((value) => (
                      <Col xs={24} sm={12} xl={8} key={value}>
                        <OptionRadio
                          value={value}
                          checked={selectedPricing === value}
                          image={carImage}
                          title={fields?.pricing?.detail?.[value]?.name}
                          description={formatCurrency(
                            Number(fields?.pricing?.detail?.[value]?.price) || 0
                          )}
                        />
                      </Col>
                    ))}
                </Row>
              </Radio.Group>
            </Spin>
          </SectionCard>
        )}

        <StepFooter
          onPrev={prevStep}
          extra={
            <Text type="secondary">
              Нийт жин: <Text strong>{totalWeight} кг</Text>
            </Text>
          }
        />
      </Space>
    </Form>
  );
};

export default Step2;
